import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Send, X, UserPlus, FilePlus2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MultiSelect } from "@/components/common/MultiSelect";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import { AddressLocationPicker } from "@/components/common/AddressLocationPicker";
import { DIAL_CODES, GENDER_OPTIONS, PATIENT_IDENTITY_TYPES } from "@/lib/dialCodes";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AttachmentsPanel from "@/pages/onboarding/AttachmentsPanel";

const EMPTY = {
  patient: { mrn: "", first_name: "", middle_name: "", last_name: "", national_id: "", identity_type: "", dob: "", gender: "", nationality: "", dial_code: "+966", phone: "", email: "", address: "", city: "" },
  referral: { source: "", facility_id: "", department: "", referring_physician: "", referral_date: "" },
  clinical: { primary_diagnosis: "", secondary_diagnosis: "", clinical_summary: "", current_medications: "", allergies: "", clinical_notes: "" },
  service: { requested_service: "", priority: "routine", expected_start_date: "" },
};

const TODAY = () => new Date().toISOString().slice(0, 10);
const NAME_RE = /^[A-Za-z\u0600-\u06FF][A-Za-z\u0600-\u06FF\s'-]{1,29}$/;
const IDENTITY_RULES = {
  "National Number": [/^1\d{9}$/, "National Number must start with 1 and be exactly 10 digits"],
  "Permanent Resident": [/^2\d{9}$/, "Permanent Resident must start with 2 and be exactly 10 digits"],
  "Border Number": [/^[34]\d{9}$/, "Border Number must be 10 digits starting with 3 or 4"],
  "Passport": [/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{5,30}$/, "Passport must be 5–30 chars, a mix of letters and numbers"],
};
const toArr = (s) => (s ? String(s).split(/\s*,\s*/).filter(Boolean) : []);
const toStr = (arr) => (arr || []).join(", ");

// Field-level business validation (mirrors backend submit gate).
function fieldError(section, field, value, ctx) {
  const v = (value ?? "").toString().trim();
  if (section === "patient") {
    switch (field) {
      case "first_name": return !v ? "Required" : (!NAME_RE.test(v) ? "No numbers or symbols" : "");
      case "last_name": return !v ? "Required" : (!NAME_RE.test(v) ? "No numbers or symbols" : "");
      case "middle_name": return v && !NAME_RE.test(v) ? "No numbers or symbols" : "";
      case "gender": return !v ? "Required" : "";
      case "nationality": return !v ? "Required" : "";
      case "dob": {
        if (!v) return "Required";
        if (v > TODAY()) return "Can't be in the future";
        const age = Math.floor((Date.now() - new Date(v).getTime()) / (365.25 * 864e5));
        if (age < 18) return "Patient must be at least 18 years old";
        if (age > 120) return "Date of birth is not valid";
        return "";
      }
      case "identity_type": return !v ? "Required" : "";
      case "national_id": {
        const itype = ctx.patient.identity_type;
        if (!itype) return "Select identity type first";
        if (!v) return "Required";
        const rule = IDENTITY_RULES[itype];
        return rule && !rule[0].test(v) ? rule[1] : "";
      }
      case "dial_code": return ctx.patient.phone && !v ? "Required" : "";
      case "phone": return !v ? "Required" : (!/^\d{6,25}$/.test(v) ? "Must be 6–25 digits" : "");
      case "email": return v && !/^\S+@\S+\.\S+$/.test(v) ? "Enter a valid email address" : "";
      default: return "";
    }
  }
  if (section === "clinical") {
    if (field === "primary_diagnosis") return !v ? "Primary diagnosis is required" : "";
    if (field === "secondary_diagnosis") return v && v === (ctx.clinical.primary_diagnosis || "").trim() ? "Must differ from primary diagnosis" : "";
    return "";
  }
  if (section === "service") {
    if (field === "requested_service") return !v ? "Required" : "";
    if (field === "priority") return !v ? "Required" : "";
    if (field === "expected_start_date") return v && v < TODAY() ? "Can't be in the past" : "";
    return "";
  }
  if (section === "referral") {
    if (field === "referral_date") return v && v < TODAY() ? "Can't be in the past" : "";
    return "";
  }
  return "";
}

const VALIDATE_FIELDS = [
  ["patient", "first_name"], ["patient", "middle_name"], ["patient", "last_name"],
  ["patient", "gender"], ["patient", "nationality"], ["patient", "dob"],
  ["patient", "identity_type"], ["patient", "national_id"], ["patient", "dial_code"], ["patient", "phone"], ["patient", "email"],
  ["clinical", "primary_diagnosis"], ["clinical", "secondary_diagnosis"],
  ["service", "requested_service"], ["service", "priority"], ["service", "expected_start_date"],
  ["referral", "referral_date"],
];

function genMrn() {
  return null; // MRN is now generated by the system after medical + operational approval
}

function flattenAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  return addr.line1 || addr.line || addr.street ||
    [addr.line1, addr.district, addr.city].filter(Boolean).join(", ") || "";
}

export default function CreateRequest({ mode = "new" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = mode === "edit" || !!id;

  // Hospital Coordinators are locked to the facility they work under.
  const lockFacility = user?.role_code === "hospital_coordinator";
  const homeFacilityId = (user?.facility_ids || [])[0] || "";

  const [form, setForm] = useState(EMPTY);
  const [request, setRequest] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [nidConflict, setNidConflict] = useState("");

  const [refSources, setRefSources] = useState([]);
  const [refDepartments, setRefDepartments] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [services, setServices] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [medications, setMedications] = useState([]);
  const [physicianSuggestions, setPhysicianSuggestions] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [existingPatients, setExistingPatients] = useState([]);
  const [patientQuery, setPatientQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [readmitInfo, setReadmitInfo] = useState(null);
  const [readmitDialogOpen, setReadmitDialogOpen] = useState(false);
  const [readmitJustification, setReadmitJustification] = useState("");

  useEffect(() => {
    async function loadLookups() {
      try {
        const cats = await api.get("/lookups/categories", { params: { page_size: 200 } });
        const findCat = (code) => cats.data.items.find((c) => c.code === code);
        const fetchValues = async (code) => {
          const cat = findCat(code);
          if (!cat) return [];
          const v = await api.get("/lookups/values", { params: { category_id: cat.id, page_size: 200 } });
          return v.data.items;
        };
        const [src, dep, pri, svc] = await Promise.all([
          fetchValues("referral_sources"), fetchValues("referral_departments"),
          fetchValues("referral_priorities"), fetchValues("requested_services"),
        ]);
        setRefSources(src); setRefDepartments(dep); setPriorities(pri); setServices(svc);
        const refPhys = await fetchValues("referring_physicians");
        setPhysicianSuggestions(refPhys.map((p) => p.label || p.value).filter(Boolean));
        const [nat, alg, dia, med] = await Promise.all([
          fetchValues("nationality"), fetchValues("patient_allergies"),
          fetchValues("diagnoses"), fetchValues("medications"),
        ]);
        setNationalities(nat); setAllergens(alg); setDiagnoses(dia); setMedications(med);
        const fac = await api.get("/facilities", { params: { page_size: 100 } });
        setFacilities(fac.data.items);
      } catch (_e) {}
    }
    loadLookups();
  }, []);


  useEffect(() => {
    if (!id) {
      setForm({ ...EMPTY });
      return;
    }
    api.get(`/onboarding/requests/${id}`).then((r) => {
      setRequest(r.data);
      const pt = r.data.patient || EMPTY.patient;
      setForm({
        patient: { ...pt, address: pt.address || {} },
        referral: r.data.referral || EMPTY.referral,
        clinical: r.data.clinical || EMPTY.clinical,
        service: r.data.service || EMPTY.service,
      });
    }).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  useEffect(() => {
    if (lockFacility && homeFacilityId) {
      setForm((f) =>
        f.referral.facility_id === homeFacilityId
          ? f
          : { ...f, referral: { ...f.referral, facility_id: homeFacilityId } }
      );
    }
  }, [lockFacility, homeFacilityId, id]);

  useEffect(() => {
    if (patientQuery.length < 2) { setExistingPatients([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/patients", { params: { q: patientQuery, page_size: 8 } });
        setExistingPatients(res.data.items);
      } catch (_e) { setExistingPatients([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [patientQuery]);

  // Detect a recently-dispositioned (returning) patient to surface a readmission hint.
  const pNid = form.patient.national_id;
  const pMrn = form.patient.mrn;
  const pId = form.patient.patient_id;
  useEffect(() => {
    if (!pNid && !pMrn && !pId) { setReadmitInfo(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/onboarding/readmit-check", {
          params: { national_id: pNid || "", mrn: pMrn || "", patient_id: pId || "" },
        });
        setReadmitInfo(res.data.readmit || null);
      } catch (_e) { setReadmitInfo(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [pNid, pMrn, pId]);

  // Live uniqueness check on the identity number (no other patient/referral may possess it).
  const idType = form.patient.identity_type;
  const pPatientId = form.patient.patient_id;
  useEffect(() => {
    const nid = (form.patient.national_id || "").trim();
    if (!nid || fieldError("patient", "national_id", nid, form)) { setNidConflict(""); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/onboarding/check-identity", {
          params: { national_id: nid, exclude_patient_id: pPatientId || "", exclude_request_id: request?.id || "" },
        });
        setNidConflict(res.data.unique ? "" : (res.data.message || "This identity number already belongs to another patient."));
      } catch (_e) { setNidConflict(""); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pNid, idType, pPatientId]);

  function setPatientFromExisting(p) {
    setForm((f) => ({
      ...f,
      patient: {
        ...f.patient,
        patient_id: p.id,
        mrn: p.mrn, first_name: p.first_name, middle_name: p.second_name || p.middle_name || "", last_name: p.last_name,
        national_id: p.national_id || p.identity_number || "", identity_type: p.identity_type || "",
        nationality: p.nationality || "",
        dob: p.dob || "", gender: p.gender || "", dial_code: p.dial_code || "+966", phone: p.phone || "", email: p.email || "",
        address: (p.address && typeof p.address === "object") ? p.address : (p.address ? { line1: p.address } : {}), city: p.city || p.address?.city || "",
      },
    }));
    setExistingPatients([]);
    setPatientQuery("");
    toast.success(`Patient ${p.first_name} ${p.last_name} loaded`);
  }

  function update(section, field, value) {
    const next = { ...form, [section]: { ...form[section], [field]: value } };
    // Nationality drives the allowed identity type (Saudi → National Number; others → no National Number)
    if (section === "patient" && field === "nationality") {
      if (value === "Saudi") next.patient.identity_type = "National Number";
      else if (value && next.patient.identity_type === "National Number") next.patient.identity_type = "";
    }
    setForm(next);
    // Live inline validation — surface issues as the user fills the form.
    const e = { ...errors };
    const setE = (sec, fl) => {
      const key = `${sec}.${fl}`;
      const msg = fieldError(sec, fl, next[sec][fl], next);
      if (msg) e[key] = msg; else delete e[key];
    };
    setE(section, field);
    if (section === "patient" && field === "identity_type") setE("patient", "national_id");
    if (section === "patient" && field === "nationality") { setE("patient", "identity_type"); setE("patient", "national_id"); }
    if (section === "patient" && field === "phone") setE("patient", "dial_code");
    if (section === "patient" && field === "dial_code") setE("patient", "phone");
    if (section === "clinical" && field === "primary_diagnosis") setE("clinical", "secondary_diagnosis");
    setErrors(e);
  }

  function validate() {
    const e = {};
    VALIDATE_FIELDS.forEach(([s, f]) => {
      const msg = fieldError(s, f, form[s][f], form);
      if (msg) e[`${s}.${f}`] = msg;
    });
    if (nidConflict && !e["patient.national_id"]) e["patient.national_id"] = nidConflict;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function save(submit = false) {
    if (submit && !validate()) { toast.error("Please fix the highlighted fields before submitting."); return; }
    setSaving(true);
    try {
      let req = request;
      if (isEdit && req) {
        const res = await api.put(`/onboarding/requests/${req.id}`, form);
        req = res.data;
      } else {
        const res = await api.post("/onboarding/requests", form);
        req = res.data;
        setRequest(req);
        // Update URL so attachments can be tied to this request id
        window.history.replaceState(null, "", `/onboarding/requests/${req.id}/edit`);
      }
      if (submit) {
        const res2 = await api.post(`/onboarding/requests/${req.id}/submit`, {});
        req = res2.data;
        toast.success("Request submitted for medical review");
        navigate(`/onboarding/requests/${req.id}`);
        return;
      }
      toast.success("Draft saved");
      setRequest(req);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  const isResubmit = request?.status === "information_requested";

  // Dropdown options (lookup values merged with any pre-existing free-text values so legacy data still renders)
  const diagOptions = useMemo(() => diagnoses.map((d) => ({ value: d.value, label: d.label })), [diagnoses]);
  // Nationality drives the allowed identity types
  const idTypeOptions = useMemo(() => {
    const nat = form.patient.nationality;
    if (nat === "Saudi") return PATIENT_IDENTITY_TYPES.filter((t) => t.value === "National Number");
    if (nat) return PATIENT_IDENTITY_TYPES.filter((t) => t.value !== "National Number");
    return [];
  }, [form.patient.nationality]);
  const mergeOptions = (lookupItems, current) => {
    const base = lookupItems.map((o) => ({ value: o.value, label: o.label }));
    toArr(current).forEach((v) => { if (!base.some((o) => o.value === v)) base.push({ value: v, label: v }); });
    return base;
  };
  const medOptions = useMemo(() => mergeOptions(medications, form.clinical.current_medications), [medications, form.clinical.current_medications]);
  const allergyOptions = useMemo(() => mergeOptions(allergens, form.clinical.allergies), [allergens, form.clinical.allergies]);

  async function saveAndReadmit() {
    if (!validate()) { toast.error("Please fix validation errors."); return; }
    if (!readmitJustification.trim()) { toast.error("A justification is required."); return; }
    setSaving(true);
    try {
      let req = request;
      if (isEdit && req) {
        const res = await api.put(`/onboarding/requests/${req.id}`, form);
        req = res.data;
      } else {
        const res = await api.post("/onboarding/requests", form);
        req = res.data;
        setRequest(req);
      }
      await api.post(`/onboarding/requests/${req.id}/readmit`, { justification: readmitJustification });
      setReadmitDialogOpen(false);
      toast.success("Readmission requested — sent to physician for approval");
      navigate(`/onboarding/requests/${req.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="ob-create-page">
      <PageHeader
        title={isEdit ? `Edit Request ${request?.ref_number || ""}` : "Create Onboarding Request"}
        subtitle={isResubmit
          ? "Address the information requested by the physician and resubmit."
          : "Initiate a Hospital @ Home referral. Save as draft or submit for medical review."}
        actions={
          <Button variant="outline" onClick={() => navigate(-1)} className="border-[#E5E7EB]" data-testid="ob-cancel-create">
            <X className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
        }
      />

      {isResubmit && request?.information_requested && (
        <div className="mb-4 p-4 rounded-lg bg-[#F59E0B14] border border-[#F59E0B33] text-[#92400E] text-sm" data-testid="ob-info-request-banner">
          <div className="font-semibold mb-1">Physician requested information</div>
          <div>{request.information_requested}</div>
          {request.information_request_notes && (
            <div className="text-xs mt-1 opacity-80">{request.information_request_notes}</div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {readmitInfo && (
          <div className="hah-card p-4 border-[#D9770633] bg-[#D9770610]" data-testid="ob-create-readmit-banner">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#D9770618] text-[#B45309] flex items-center justify-center shrink-0">
                <RotateCcw className="w-4 h-4" />
              </div>
              <div className="text-sm text-[#92400E]">
                <div className="font-semibold mb-0.5">Returning patient — eligible for fast-track readmission</div>
                <div>
                  {readmitInfo.patient_name || "This patient"} was dispositioned {readmitInfo.hours_since}h ago
                  ({readmitInfo.disposition_label || "—"}). Fill in the remaining details, then click
                  <strong> Readmit</strong> to send it to an H@H physician for approval — about <strong>{readmitInfo.hours_remaining}h</strong> left in the 48-hour window.
                </div>
                <Button
                  type="button"
                  className="mt-3 bg-[#D97706] hover:bg-[#B45309] text-white"
                  data-testid="ob-create-readmit-btn"
                  onClick={() => setReadmitDialogOpen(true)}
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Readmit
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Patient lookup */}
        {!isEdit && (
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Patient lookup</div>
            <h3 className="text-base font-semibold text-[#1F2937]">Search existing patient or create new</h3>
            <div className="mt-3 relative">
              <Input
                data-testid="ob-patient-search"
                placeholder="Search by name or MRN…"
                value={patientQuery}
                onChange={(e) => setPatientQuery(e.target.value)}
              />
              {existingPatients.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-md max-h-72 overflow-y-auto">
                  {existingPatients.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPatientFromExisting(p)}
                      data-testid={`ob-patient-pick-${p.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[#EAF8FA]/50 flex items-center gap-3 border-b border-[#E5E7EB] last:border-b-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
                        {(p.first_name?.[0] || "?") + (p.last_name?.[0] || "")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#1F2937] truncate">{p.first_name} {p.last_name}</div>
                        <div className="text-xs text-[#6B7280] truncate">
                          {p.mrn} · {p.national_id ? `ID ${p.national_id}` : "No ID"}{p.nationality ? ` · ${p.nationality}` : ""} · DOB {p.dob || "—"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {!searching && patientQuery.length >= 2 && existingPatients.length === 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-md p-3" data-testid="ob-patient-empty">
                  <div className="text-sm text-[#6B7280] mb-2">No patient found for "{patientQuery}".</div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#0B7E8C33] text-[#0B7E8C] hover:bg-[#EAF8FA]"
                    data-testid="ob-patient-create-new"
                    onClick={() => { setForm((f) => ({ ...f, patient: { ...f.patient, patient_id: "" } })); setExistingPatients([]); setPatientQuery(""); }}
                  >
                    <UserPlus className="w-4 h-4 mr-1.5" /> Create new patient request
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Patient info */}
        <FormSection title="Patient Information" description="Identity and contact details of the patient." testId="ob-section-patient">
          <FormField label="Nationality" required error={errors["patient.nationality"]} hint="Drives the identity type below.">
            <Select value={form.patient.nationality || ""} onValueChange={(v) => update("patient", "nationality", v)}>
              <SelectTrigger data-testid="ob-patient-nationality"><SelectValue placeholder="Select nationality" /></SelectTrigger>
              <SelectContent>
                {nationalities.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Identity type" required error={errors["patient.identity_type"]}
            hint={!form.patient.nationality ? "Select nationality first." : (form.patient.nationality === "Saudi" ? "Saudi nationals use a National Number." : "Passport, Permanent Resident or Border Number.")}>
            <Select value={form.patient.identity_type || ""} onValueChange={(v) => update("patient", "identity_type", v)}
              disabled={!form.patient.nationality || form.patient.nationality === "Saudi"}>
              <SelectTrigger data-testid="ob-patient-identity-type" className={(!form.patient.nationality || form.patient.nationality === "Saudi") ? "opacity-70 bg-[#F3F4F6]" : ""}>
                <SelectValue placeholder={form.patient.nationality ? "Select" : "Select nationality first"} />
              </SelectTrigger>
              <SelectContent>
                {idTypeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="National ID / Iqama" required error={errors["patient.national_id"] || nidConflict}
            hint={form.patient.identity_type === "National Number" ? "Starts with 1, 10 digits, must be unique." : "Used to detect returning H@H patients."}>
            <Input data-testid="ob-patient-nid" disabled={!form.patient.identity_type}
              placeholder={!form.patient.identity_type ? "Select identity type first" : ""}
              value={form.patient.national_id || ""} onChange={(e) => update("patient", "national_id", e.target.value.trim())} />
          </FormField>
          <FormField label="First name" required error={errors["patient.first_name"]}>
            <Input data-testid="ob-patient-first" value={form.patient.first_name} onChange={(e) => update("patient", "first_name", e.target.value)} />
          </FormField>
          <FormField label="Middle name" error={errors["patient.middle_name"]}>
            <Input data-testid="ob-patient-middle" value={form.patient.middle_name || ""} onChange={(e) => update("patient", "middle_name", e.target.value)} />
          </FormField>
          <FormField label="Last name" required error={errors["patient.last_name"]}>
            <Input data-testid="ob-patient-last" value={form.patient.last_name} onChange={(e) => update("patient", "last_name", e.target.value)} />
          </FormField>
          <FormField label="Date of birth" required error={errors["patient.dob"]} hint="18+ years; no future dates.">
            <Input data-testid="ob-patient-dob" type="date" max={TODAY()} value={form.patient.dob || ""} onChange={(e) => update("patient", "dob", e.target.value)} />
          </FormField>
          <FormField label="Gender" required error={errors["patient.gender"]}>
            <Select value={form.patient.gender || ""} onValueChange={(v) => update("patient", "gender", v)}>
              <SelectTrigger data-testid="ob-patient-gender"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Mobile number" required full error={errors["patient.phone"] || errors["patient.dial_code"]} hint="Select the country dial code first.">
            <div className="flex gap-2">
              <Select value={form.patient.dial_code || ""} onValueChange={(v) => update("patient", "dial_code", v)}>
                <SelectTrigger data-testid="ob-patient-dial-code" className="w-[150px] shrink-0"><SelectValue placeholder="Code" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {DIAL_CODES.map((d) => <SelectItem key={d.iso3} value={d.code}>{d.code} · {d.country}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid="ob-patient-phone" className="flex-1" disabled={!form.patient.dial_code}
                placeholder={!form.patient.dial_code ? "Select dial code first" : "Phone number"}
                value={form.patient.phone || ""} onChange={(e) => update("patient", "phone", e.target.value)} />
            </div>
          </FormField>
          <FormField label="Email" error={errors["patient.email"]}>
            <Input data-testid="ob-patient-email" type="email" value={form.patient.email || ""} onChange={(e) => update("patient", "email", e.target.value)} />
          </FormField>
          <FormField label="Home address & map location" full>
            <AddressLocationPicker
              value={form.patient.address}
              onChange={(addr) => setForm((f) => ({ ...f, patient: { ...f.patient, address: addr, city: addr.city || f.patient.city } }))}
              testidPrefix="ob-addr"
            />
          </FormField>
        </FormSection>

        {/* Referral */}
        <FormSection title="Referral Information" testId="ob-section-referral" description="Who is referring this patient.">
          <FormField label="Referral source">
            <Select value={form.referral.source || ""} onValueChange={(v) => update("referral", "source", v)}>
              <SelectTrigger data-testid="ob-ref-source"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {refSources.map((s) => <SelectItem key={s.id} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Referral facility">
            <Select value={form.referral.facility_id || ""} onValueChange={(v) => update("referral", "facility_id", v)}>
              <SelectTrigger data-testid="ob-ref-facility"><SelectValue placeholder="Select facility" /></SelectTrigger>
              <SelectContent>
                {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Department">
            <Select value={form.referral.department || ""} onValueChange={(v) => update("referral", "department", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {refDepartments.map((d) => <SelectItem key={d.id} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Referring physician">
            <SearchableSelect
              value={form.referral.referring_physician || ""}
              onChange={(v) => update("referral", "referring_physician", v)}
              options={physicianSuggestions.map((n) => ({ value: n, label: n }))}
              allowCustom
              placeholder="Select or search a physician…"
              testid="ob-referring-physician"
            />
          </FormField>
          <FormField label="Referral date" error={errors["referral.referral_date"]}>
            <Input type="date" min={TODAY()} value={form.referral.referral_date || ""} onChange={(e) => update("referral", "referral_date", e.target.value)} />
          </FormField>
        </FormSection>

        {/* Clinical */}
        <FormSection title="Clinical Information" testId="ob-section-clinical" description="Diagnoses, history and clinical context. Pick from the list or type to add your own.">
          <FormField label="Primary diagnosis" required error={errors["clinical.primary_diagnosis"]} full>
            <SearchableSelect testid="ob-clinical-primary" options={diagOptions} allowCustom
              value={form.clinical.primary_diagnosis} onChange={(v) => update("clinical", "primary_diagnosis", v)}
              placeholder="Select or search a diagnosis…" />
          </FormField>
          <FormField label="Secondary diagnosis" error={errors["clinical.secondary_diagnosis"]} full>
            <SearchableSelect testid="ob-clinical-secondary" options={diagOptions} allowCustom
              value={form.clinical.secondary_diagnosis || ""} onChange={(v) => update("clinical", "secondary_diagnosis", v)}
              placeholder="Select or search a diagnosis…" />
          </FormField>
          <FormField label="Clinical summary" full>
            <Textarea rows={3} value={form.clinical.clinical_summary || ""} onChange={(e) => update("clinical", "clinical_summary", e.target.value)} />
          </FormField>
          <FormField label="Current medications" full hint="Multi-select. Type to add a medication that isn't listed.">
            <MultiSelect testid="ob-clinical-medications" options={medOptions} values={toArr(form.clinical.current_medications)}
              onChange={(arr) => update("clinical", "current_medications", toStr(arr))} placeholder="Select medications…" showPrimaryBadge={false} />
          </FormField>
          <FormField label="Allergies" full hint="Multi-select. Type to add an allergen that isn't listed.">
            <MultiSelect testid="ob-clinical-allergies" options={allergyOptions} values={toArr(form.clinical.allergies)}
              onChange={(arr) => update("clinical", "allergies", toStr(arr))} placeholder="Select allergies…" showPrimaryBadge={false} />
          </FormField>
          <FormField label="Clinical notes" full>
            <Textarea rows={2} value={form.clinical.clinical_notes || ""} onChange={(e) => update("clinical", "clinical_notes", e.target.value)} />
          </FormField>
        </FormSection>

        {/* Service */}
        <FormSection title="Service Information" testId="ob-section-service" description="What service is being requested.">
          <FormField label="Requested service" required error={errors["service.requested_service"]}>
            <Select value={form.service.requested_service || ""} onValueChange={(v) => update("service", "requested_service", v)}>
              <SelectTrigger data-testid="ob-service"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {services.map((s) => <SelectItem key={s.id} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Priority" required error={errors["service.priority"]}>
            <Select value={form.service.priority || "routine"} onValueChange={(v) => update("service", "priority", v)}>
              <SelectTrigger data-testid="ob-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {priorities.length > 0 ? priorities.map((p) => (
                  <SelectItem key={p.id} value={p.value}>{p.label}</SelectItem>
                )) : (
                  <>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="stat">STAT</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Expected start date" error={errors["service.expected_start_date"]}>
            <Input type="date" min={TODAY()} value={form.service.expected_start_date || ""} onChange={(e) => update("service", "expected_start_date", e.target.value)} />
          </FormField>
        </FormSection>

        {/* Attachments (only when request exists / has id) */}
        {request?.id && (
          <AttachmentsPanel requestId={request.id} />
        )}
        {!request?.id && (
          <div className="hah-card p-5 text-sm text-[#6B7280]" data-testid="ob-attachments-hint">
            Save the request as a draft first to enable attachments (referral form, clinical documents, lab/radiology reports).
          </div>
        )}
      </div>

      <StickyActionBar testId="ob-create-actions">
        <Button variant="outline" onClick={() => navigate("/onboarding")} className="border-[#E5E7EB]" data-testid="ob-cancel">
          Cancel
        </Button>
        <Button onClick={() => save(false)} disabled={saving} variant="outline" className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid="ob-save-draft">
          <Save className="w-4 h-4 mr-1.5" /> Save Draft
        </Button>
        {readmitInfo && (
          <Button onClick={() => setReadmitDialogOpen(true)} disabled={saving} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="ob-actionbar-readmit">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Readmit
          </Button>
        )}
        <Button onClick={() => save(true)} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-submit">
          <Send className="w-4 h-4 mr-1.5" /> {isResubmit ? "Resubmit" : "Submit for Review"}
        </Button>
      </StickyActionBar>

      <Dialog open={readmitDialogOpen} onOpenChange={setReadmitDialogOpen}>
        <DialogContent data-testid="ob-create-readmit-dialog">
          <DialogHeader>
            <DialogTitle>Request readmission</DialogTitle>
            <DialogDescription>
              {readmitInfo?.patient_name || "This patient"} was dispositioned {readmitInfo?.hours_since}h ago
              ({readmitInfo?.disposition_label || "—"}). This saves the request and sends it to an H@H physician
              for readmission approval (≈{readmitInfo?.hours_remaining}h left in the 48-hour window).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1F2937]">Justification <span className="text-[#EF4444]">*</span></label>
            <Textarea rows={4} value={readmitJustification} onChange={(e) => setReadmitJustification(e.target.value)} placeholder="Clinical reason for readmitting within 48 hours…" data-testid="ob-create-readmit-justification" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReadmitDialogOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={saveAndReadmit} disabled={saving} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="ob-create-readmit-confirm">
              <RotateCcw className="w-4 h-4 mr-1.5" /> {saving ? "Submitting…" : "Submit for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
